import { z } from 'zod';
import { ChannelKindSchema, ConversationStatusValueSchema, ReclassifyScopeSchema, SenderCapacitySchema, } from '@wit-support/contracts';
import { PipelineRunStepDTO } from './pipelines.js';
import { TagRefDTO } from './tags.js';
import { CommentPreviewDTO } from './comments.js';
/**
 * `ConversationId` lives in the leaf module `./ids.js`, NOT here — this file
 * (`conversations.ts`) imports `CommentPreviewDTO` from `comments.ts`, and
 * `comments.ts` needs `ConversationId`. Declaring it here would make the two
 * files import each other, and whichever one's top-level body runs first
 * (order depends on which module a caller imports FIRST — see
 * `comments.test.ts`) would read the other's binding before it's
 * initialized: this broke `CommentDTO`/`CommentFeedItemDTO` at commit
 * 3ae3192 (`z.lazy` alone only protected THIS file's reference, not
 * `comments.ts`'s eager one).
 *
 * Imported (for local use throughout this file) AND re-exported (so every
 * existing `import { ConversationId } from './conversations.js'` /
 * `from '@wit-support/admin-contracts'` keeps working unchanged).
 */
import { ConversationId } from './ids.js';
export { ConversationId };
/**
 * Admin-facing Zod schemas for the read-only conversations inbox.
 *
 * Scope: lists, single conversation + contact, message list per conversation.
 * No reply composer / mutations — those ship when the admin-actions package
 * lands in a later phase.
 */
/** Re-exported from `@wit-support/contracts` — canonical source of truth. */
export const ConversationStatus = ConversationStatusValueSchema;
/** Re-exported from `@wit-support/contracts` — canonical source of truth. */
export const ConversationChannelKind = ChannelKindSchema;
/**
 * Body of `PATCH /api/conversations/:id` — manually move a conversation to a new
 * lifecycle status. The DB trigger `conversation_stamp_lifecycle_at` stamps
 * `resolved_at` / `closed_at` server-side, so the operator only chooses the
 * target status. Response is the updated {@link ConversationDTO} (mirrors the
 * GET `/:id` shape).
 */
export const UpdateConversationRequest = z.object({
    status: ConversationStatus,
    /**
     * Optional operator note explaining the status change — persisted as an
     * internal {@link CommentDTO} linked to the resulting status-history row
     * (`comment.statusEventId`). Omitted means no note is recorded.
     */
    comment: z.string().trim().min(1).max(4000).optional(),
});
export const ConversationListItemDTO = z.object({
    id: ConversationId,
    /**
     * Короткий человекочитаемый номер обращения (`conversation.number`) — тот же,
     * что в шапке диалога и в письме под токеном `{{conversation.number}}`. В
     * строке списка он нужен, чтобы после поиска по номеру было видно, КАКАЯ из
     * строк найдена. Nullable-with-default по той же причине, что и в
     * {@link ConversationDTO}: в окне выкатки старый admin-api поля ещё не отдаёт.
     */
    number: z.number().int().positive().nullable().default(null),
    status: ConversationStatus,
    channelKind: ConversationChannelKind,
    channelAccountId: z.guid().nullable(),
    subject: z.string().nullable(),
    lastMessageSnippet: z.string().nullable(),
    /** Number of attachments on the latest message — lets the inbox list render
     *  a "📎 N files" hint when the body is empty (e.g. image-only email). */
    lastMessageAttachmentCount: z.number().int().nonnegative().default(0),
    /** direction of the LATEST message — 'out' = sent by us/agent, 'in' = from the
     *  customer; drives the inbox 'from us' marker. */
    lastMessageDirection: z.enum(['in', 'out']).nullable(),
    lastMessageAt: z.string().datetime().nullable(),
    /**
     * Always 0 today — there's no per-user read-tracking column on `message`
     * yet. Wired into the DTO so the shape doesn't change when a future
     * migration adds it.
     */
    unreadCount: z.number().int().min(0),
    contact: z
        .object({
        id: z.guid(),
        displayName: z.string().nullable(),
        primaryEmail: z.string().nullable(),
    })
        .nullable(),
    /**
     * Compact classification labels for the inbox row — the conversation's
     * `conversation_topic` rows joined to their (non-deleted) `topic`. Drops
     * `confidence` (carried only on the single-conversation {@link ConversationDTO})
     * so the list payload stays lean. Empty when unclassified. Primary topic first.
     */
    topics: z
        .array(z.object({
        key: z.string(),
        name: z.string(),
        isPrimary: z.boolean(),
    }))
        .default([]),
    /**
     * True when this conversation has ANY pipeline run that did something wrong
     * (run failed OR a step in the canonical error set). Drives the inbox row's
     * ⚠ indicator. A persistent "there was a problem here" mark — not reset by a
     * later successful run (pipelines are effectively never re-run here).
     */
    pipelineErrored: z.boolean().default(false),
    /**
     * True when this conversation has a pipeline run parked (`waiting`) on a
     * customer reply. Drives the inbox row's amber marker, so the one state where
     * the bot is holding the dialogue open is visible WITHOUT opening the chat.
     *
     * Deliberately a boolean and not the deadline: the list payload stays lean,
     * and «до какого числа» is a detail the operator reads in the rail. Unlike
     * {@link pipelineErrored} this is LIVE — it disappears the moment the run
     * resumes, times out, or an operator answers by hand.
     *
     * `default(false)` for the rollout window, like every other list-item field.
     */
    pipelineWaiting: z.boolean().default(false),
    /** In what capacity the sender writes; null when unclassified. `business` drives
     *  the «Коммерческое» badge and (behind the flag) suppresses auto-pipelines. */
    senderCapacity: SenderCapacitySchema.nullable().default(null),
    /**
     * Сколько кейсов платёжного риска (`risk_case`) связано с контактом этой
     * строки — по `contact.wit_user_id` ИЛИ по email алерта (связь не
     * материализована, спека §3.4). Драйвит маркер в строке инбокса.
     *
     * `.nullable().default(null)` и читается ТОЛЬКО через `!= null`, как `number`
     * и `senderCapacity`: в окне выката новый бандл уже читает поле, которого
     * старый admin-api ещё не отдаёт, и `undefined > 0` нарисовало бы
     * маркер-призрак. `null` = «не знаем», `0` = «знаем, что алертов нет» — это
     * РАЗНЫЕ факты, и схлопывать их нельзя.
     */
    chargebackCaseCount: z.number().int().nonnegative().nullable().default(null),
    /**
     * Крупный ли это плательщик («кит») — число успешных списаний у контакта
     * достигло порога `billing.whale_min_charges`. Драйвит бейдж в строке инбокса.
     *
     * Решение принимает СЕРВЕР, а не строка: порог живёт в `app_settings` и
     * читается один раз на запрос списка. Клиенту в этом поле нужен ответ «да/нет»,
     * а не порог — иначе каждая строка пересчитывала бы одно и то же, а сам порог
     * пришлось бы тащить в списочный ответ вторым полем и держать в синхроне.
     *
     * `.nullable().default(null)` и читается ТОЛЬКО через `=== true`, по той же
     * причине, что `chargebackCaseCount`: `null` оставлен ровно за одним фактом —
     * «старый admin-api поля не отдал».
     *
     * Неизвестное число списаний (контакта ещё ни разу не обогащали биллингом)
     * даёт `false`, а НЕ `null` — так отвечает общий `isWhale` из
     * `@wit-support/db`, и второй ответ на тот же вопрос здесь развёл бы канон.
     * Промолчать правильнее, чем назвать китом того, про кого ничего не известно.
     * Различить «не платил» и «не спрашивали» позволяет
     * {@link succeededChargesCount} — это его работа, не этого поля.
     */
    isWhale: z.boolean().nullable().default(null),
    /**
     * Число успешных списаний у контакта (`contact_subscription.succeeded_charges_count`)
     * — то самое, по которому вычислен {@link isWhale}.
     *
     * Нужно РОВНО для подписи бейджа («Крупный плательщик: 47 списаний»): без
     * числа оператор видит метку и не понимает, почему она стоит. Отдельным полем,
     * а не заменой `isWhale` на число, потому что порог сравнения — серверный, и
     * строка не должна знать про `billing.whale_min_charges`.
     *
     * `null` = «неизвестно» (нет строки `contact_subscription`, счётчик в ней
     * NULL, диалог без контакта, или старый admin-api). Именно это поле, а не
     * {@link isWhale}, отличает «не платил» от «не спрашивали»: `0` — знание,
     * `null` — его отсутствие.
     */
    succeededChargesCount: z.number().int().nonnegative().nullable().default(null),
    /**
     * Operator-attached tags (free-form vocabulary, `packages/admin-contracts/src/tags.ts`
     * — distinct from the LLM `topics` above). `.nullable().default(null)` for
     * the same rollout-window reason as {@link chargebackCaseCount}: a new
     * bundle can read a field an old admin-api doesn't send yet, and `null`
     * ("we don't know") must not collapse into an empty tag list ("we know
     * there are none") — the row would render as untagged.
     */
    tags: z.array(TagRefDTO).nullable().default(null),
    /**
     * Total internal comments on this conversation. Same nullable-default
     * rollout-window contract as {@link chargebackCaseCount} — read only via
     * `!= null`.
     */
    commentCount: z.number().int().nonnegative().nullable().default(null),
    /**
     * Most recent internal comment, for the inbox row's snippet — kept lean
     * ({@link CommentPreviewDTO}, not the full {@link CommentDTO}) since the
     * list payload doesn't need `id`/`statusEventId`. Same nullable-default
     * rollout-window contract as {@link chargebackCaseCount}.
     */
    lastComment: CommentPreviewDTO.nullable().default(null),
    updatedAt: z.string().datetime(),
});
export const ConversationListResponse = z.object({
    items: z.array(ConversationListItemDTO),
    /**
     * OPAQUE keyset cursor — `${updatedAtISO}|${conversationId}`, NOT a plain
     * datetime. Clients must echo it back verbatim and never parse it. Composite
     * because a timestamp-only cursor drops every row of a tie block that doesn't
     * fit on one page (see `apps/admin-api/src/adapters/keyset.ts`).
     */
    nextCursor: z.string().min(1).nullable(),
});
/**
 * Query-string contract of `GET /api/conversations` (the inbox list). Field
 * names match the WIRE parameter names (`channel`, `status`, ...) — repeated
 * params arrive as arrays, since the route collects them via `c.req.queries()`
 * and calls `.safeParse` manually rather than `@hono/zod-validator('query')`,
 * which flattens duplicates down to a single value (see the route's comment).
 *
 * Consolidated from the route's hand-written inline `z.object({...})`
 * (FP-33) — kept byte-for-byte in sync with it: `z.string().uuid()` /
 * `z.string().datetime()`, NOT `z.guid()` (parity with the route's pre-existing
 * validation, not the branded-id convention used elsewhere in this file).
 * `cursor` is deliberately NOT `z.string().datetime()` — it's the OPAQUE
 * keyset cursor (`${updatedAtISO}|${conversationId}`) introduced alongside the
 * tiebreaker, with a one-release allowance for the pre-tiebreaker bare-ISO
 * format still held by an open tab across the deploy.
 */
export const ConversationListQuery = z.object({
    channel: z.array(ConversationChannelKind).optional(),
    channelAccountId: z.array(z.string().uuid()).optional(),
    status: z.array(ConversationStatus).optional(),
    topic: z.array(z.string().min(1).max(100)).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    q: z.string().min(1).max(200).optional(),
    contactId: z.string().uuid().optional(),
    // Repeated param, like `channelAccountId` above — tag IDs, not names (the
    // list filter joins against `conversation_tag`).
    tag: z.array(z.string().uuid()).optional(),
    // Strict coercion: only the literals 'true'/'false' are accepted — any other
    // value fails the enum (400), rather than silently collapsing to `false`.
    hasScheduledEmail: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    // Same strict-enum rule as hasScheduledEmail — "big payer" (`isWhale`)
    // filter, mirroring `ConversationListItemDTO.isWhale`.
    isWhale: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    // Same strict-enum rule — restrict to conversations with at least one
    // payment-risk case, mirroring `ConversationListItemDTO.chargebackCaseCount`.
    hasChargeback: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    // Same strict-enum rule — restrict to conversations with at least one
    // internal comment, mirroring `ConversationListItemDTO.commentCount`.
    hasComment: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    // «Ждёт нашего ответа» — same strict enum as hasScheduledEmail above, for the
    // same reason: a typo must 400, not silently mean `false` and hand the
    // operator a queue that quietly excludes the rows they came for.
    awaitingOurReply: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    // OPAQUE cursor — `${ISO}|${id}` since the tiebreaker landed. Must NOT be
    // `z.string().datetime()` any more, but must still accept a bare ISO: a tab
    // open across the deploy holds an old-format cursor and «Load more» would
    // 400. The adapter parses both and ignores anything unparseable.
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const MessageDTO = z.object({
    id: z.guid(),
    direction: z.enum(['in', 'out']),
    senderKind: z.enum(['contact', 'agent', 'system', 'bot']),
    bodyText: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    subject: z.string().nullable(),
    externalMessageId: z.string().nullable(),
    createdAt: z.string().datetime(),
    attachments: z.array(z.object({
        id: z.guid(),
        // DB columns `filename`, `content_type`, and `size_bytes` are NOT NULL
        // (see `db/schema.sql`), so the DTO reflects that — callers can rely on
        // these without null-guards. Inline attachments without a filename get
        // a placeholder from the ingestion adapter.
        filename: z.string(),
        contentType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        inline: z.boolean(),
    })),
    // Retained as a server-computed API affordance for external consumers and
    // potential future UI gating (e.g. translation-available badge without a
    // separate fetch). The current in-bubble translation flow (TranslatableThread)
    // is driven entirely by the batch GET /conversations/:id/translations endpoint
    // + its translateMap — the admin UI does NOT read this field. Its presence is
    // intentional (not a stale artifact): dropping it would be a breaking API
    // change for external integrators without delivering any current-client benefit.
    hasTranslation: z.boolean().default(false),
});
export const ConversationDTO = z.object({
    id: ConversationId,
    /**
     * Короткий человекочитаемый номер обращения (`conversation.number`). Виден в
     * шапке диалога, ищется в инбоксе, подставляется в письма токеном
     * `{{conversation.number}}`. В БД колонка NOT NULL, но поле объявлено
     * nullable-with-default — как и остальные добавленные позже поля DTO
     * (`subjectTranslated`, `senderCapacity`), чтобы старые фикстуры и ответы
     * до-миграционного сервера продолжали разбираться.
     */
    number: z.number().int().positive().nullable().default(null),
    status: ConversationStatus,
    channelKind: ConversationChannelKind,
    channelAccountId: z.guid().nullable(),
    subject: z.string().nullable(),
    subjectTranslated: z.string().nullable().default(null),
    contact: z
        .object({
        id: z.guid(),
        displayName: z.string().nullable(),
        primaryEmail: z.string().nullable(),
        identities: z.array(z.object({
            kind: z.string(),
            value: z.string(),
        })),
        /**
         * Identity-resolution facts surfaced in the rail. `witUserId` is the
         * denormalized `contact.wit_user_id`; `gender`/`age` come from the latest
         * `found` resolution row (age derived from birthdate at query time).
         * All null when the contact was never resolved to a WIT user.
         */
        witUserId: z.string().nullable().default(null),
        gender: z.string().nullable().default(null),
        age: z.number().int().nonnegative().nullable().default(null),
    })
        .nullable(),
    /**
     * Set when this conversation was minted as a child of a locked (resolved
     * >30d or closed) conversation by the threading resolver. The client surfaces
     * a "continued from…" banner so operators see the prior context.
     * See `docs/architecture/threading.md`.
     */
    parentConversationId: ConversationId.nullable().default(null),
    /**
     * Parent's `subject_base` — resolved server-side in a LEFT JOIN so the UI
     * can render a meaningful link label without a second round trip.
     */
    parentConversationSubject: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    topics: z
        .array(z.object({
        key: z.string(),
        name: z.string(),
        confidence: z.number().nullable(),
        isPrimary: z.boolean(),
        rationale: z.string().nullable().default(null),
        evidence: z.array(z.string()).default([]),
    }))
        .default([]),
    classifiedAt: z.string().nullable().default(null),
    senderCapacity: SenderCapacitySchema.nullable().default(null),
    senderCapacityRationale: z.string().nullable().default(null),
    /**
     * Ручное переопределение вердикта модели о capacity (supervisor+).
     * `null` — переопределения нет. `value` — то, что выбрал человек; сам бейдж
     * рисуется по ЭФФЕКТИВНОМУ `senderCapacity`, а это поле нужно, чтобы
     * показать «изменено вручную» и кем.
     *
     * `.nullable().default(null)`, как `subjectTranslated` и `number`: во время
     * окна выката новый бандл читает поле, которого старый admin-api ещё не
     * отдаёт.
     */
    senderCapacityOverride: z
        .object({
        value: SenderCapacitySchema,
        at: z.string().datetime(),
        actorLabel: z.string().nullable(),
    })
        .nullable()
        .default(null),
    gate: z
        .object({
        eligible: z.boolean(),
        topicKey: z.string().nullable(),
        reason: z.string(),
        classifiedAt: z.string().datetime(),
    })
        .nullable()
        .default(null),
    /**
     * The pipeline run currently parked (`waiting`) on a reply from this
     * conversation's CONTACT, or null. Drives the composer banner that warns the
     * operator a bot is mid-dialogue — and that answering by hand will stop it
     * (`conversation.pipeline_operator_took_over`).
     *
     * SCOPED BY CONTACT, not by dialog, because the takeover it warns about is:
     * `announceOperatorTakeover` releases EVERY parked run of the contact
     * (`listWaitingRunsForContactOfConversation`). A dialog-scoped warning left
     * the case it exists to cover wide open — the operator answers an unrelated
     * question in dialog B of the same person and silently kills the automation
     * parked in dialog A, with no banner ever shown. The warning has to reach as
     * far as the effect does.
     *
     * Singular, not a list, and that is a UI choice rather than a schema fact:
     * `pipeline_run_one_active` is unique per (pipeline, conversation), so several
     * parks can coexist across a contact's dialogs. The banner names the OLDEST
     * one (`created_at`, then `id`) — the same run the takeover releases first —
     * and the consequence it states («ваш ответ снимает автоматику») applies to
     * every parked run alike, so a second warning would only repeat itself.
     *
     * `waitDeadline` is nullable independently of the run: the column is nullable
     * in the schema even though the parker always sets it.
     *
     * `conversationNumber` is `conversation.number` of the dialog the park
     * actually lives in, and is NULL when that is THIS dialog — the front end
     * reads the null as «здесь» and only names a number when the operator is
     * about to affect a conversation they are not looking at. Nullable + default
     * also covers the rollout window, when a new bundle can read a field the
     * deployed admin-api does not send yet.
     */
    waitingRun: z
        .object({
        id: z.guid(),
        pipelineName: z.string(),
        waitDeadline: z.string().datetime().nullable(),
        conversationNumber: z.number().int().nullable().default(null),
    })
        .nullable()
        .default(null),
    /**
     * Operator-attached tags — same field and same nullable-default
     * rollout-window contract as {@link ConversationListItemDTO.tags}.
     */
    tags: z.array(TagRefDTO).nullable().default(null),
    /** Same field and rollout-window contract as {@link ConversationListItemDTO.commentCount}. */
    commentCount: z.number().int().nonnegative().nullable().default(null),
    /** Same field and rollout-window contract as {@link ConversationListItemDTO.lastComment}. */
    lastComment: CommentPreviewDTO.nullable().default(null),
});
export const MessageListResponse = z.object({
    items: z.array(MessageDTO),
    nextCursor: z.string().datetime().nullable(),
});
/**
 * Who/what caused a `conversation.status` change, denormalized onto the
 * `conversation_event` row (`event_type='status_changed'`) at write time —
 * mirrors `StatusChangeSource` in `@wit-support/db`.
 */
export const StatusChangeSourceSchema = z.enum([
    'admin_panel',
    'telegram',
    'pipeline',
    'auto_reopen_inbound',
    'auto_close_lock',
    'blacklist',
    'created',
    // Merge-all conversations for one contact/tag — see `docs/architecture/threading.md`.
    // The merge engine writes `status='closed'` on every merged-away source
    // conversation (mirrors the existing single-merge tombstone rule) with this
    // source so the status-history popup can tell it apart from an operator's
    // manual close.
    'merge',
]);
/**
 * One row of a conversation's status-change history, read from
 * `conversation_event` (`event_type='status_changed'`). Backs
 * `GET /api/conversations/:id/status-history`.
 */
export const StatusHistoryEntrySchema = z.object({
    id: z.string(),
    from: ConversationStatus.nullable(),
    to: ConversationStatus,
    actorType: z.enum(['admin', 'pipeline', 'system']),
    actorId: z.string().nullable(),
    actorLabel: z.string(),
    source: StatusChangeSourceSchema,
    createdAt: z.string(),
    /**
     * The operator note left alongside this status change, if any — the same
     * text as `UpdateConversationRequest.comment` for this transition, resolved
     * from the linked internal {@link CommentDTO} (`comment.statusEventId`).
     * `.nullable().default(null)`: most transitions carry no comment, and old
     * history rows predate the feature entirely.
     */
    comment: z.string().nullable().default(null),
});
/** Outcome of a single identity-resolution attempt (mirrors the DB enum). */
export const IdentityResolutionResultDTO = z.enum(['found', 'no_match', 'error']);
/**
 * One row of the read-only identity-resolution log — every lookup against the
 * WIT identity service, recorded for audit. Returned by
 * `GET /api/contacts/:id/identity-resolutions` and the per-conversation variant.
 */
export const IdentityResolutionDTO = z.object({
    id: z.guid(),
    createdAt: z.string().datetime(),
    lookupEmail: z.string(),
    result: IdentityResolutionResultDTO,
    witUserId: z.string().nullable(),
    gender: z.string().nullable(),
    /** Age in whole years derived from the stored birthdate, or null. */
    age: z.number().int().nonnegative().nullable(),
    displayName: z.string().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    errorMessage: z.string().nullable(),
});
export const IdentityResolutionListResponse = z.object({
    items: z.array(IdentityResolutionDTO),
});
/**
 * One pipeline an operator may MANUALLY trigger for a conversation: its trigger
 * expression matched the conversation's classification labels. `enabled === false`
 * marks a pipeline that auto-runs are disabled for but a manual run is still
 * allowed. Returned by `GET /api/conversations/:id/pipelines`.
 */
export const EligiblePipelineDTO = z.object({
    key: z.string(),
    name: z.string(),
    enabled: z.boolean(),
});
export const EligiblePipelinesResponse = z.object({
    pipelines: z.array(EligiblePipelineDTO),
});
/**
 * Ack returned by `POST /api/conversations/:id/pipelines/:key/run` — the run was
 * accepted and enqueued (via the event outbox); the actual execution happens
 * asynchronously in the worker. The card re-renders from DB state once the run
 * progresses.
 */
export const RunPipelineAck = z.object({
    accepted: z.literal(true),
    conversationId: ConversationId,
    pipelineKey: z.string(),
});
/**
 * One historical pipeline RUN on a conversation — the pipeline name plus the
 * run's lifecycle status and timing. Returned newest-first by
 * `GET /api/conversations/:id/pipelines/runs`. `finishedAt`/`durationMs` are
 * null while the run is still in-flight ('running'); `durationMs` is the
 * `finishedAt - createdAt` span in milliseconds for terminal runs.
 */
export const ConversationPipelineRunDTO = z.object({
    /** `pipeline_run.id` — used to fetch the run's per-step detail (expandable row). */
    id: z.guid(),
    name: z.string(),
    status: z.string(),
    createdAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    /**
     * When a `waiting` run stops being suspended and escalates
     * (`pipeline_run.wait_deadline`); null for every other status. A parked run
     * has no duration to print — «ждём клиента до …» is the only fact about it an
     * operator can act on.
     *
     * Named for the RUN's wait, not for what it is waiting FOR: the column lives
     * on `pipeline_run`, so a future suspending node reuses this field as-is.
     *
     * `nullable().default(null)` for the rollout window: a new bundle must not
     * throw against an admin-api that does not send the field yet.
     */
    waitDeadline: z.string().datetime().nullable().default(null),
    /**
     * True when the run failed OR has any error-status step (incl. a `completed`
     * run that branched on a failure outcome). Drives the rail's warning treatment.
     */
    hadError: z.boolean(),
});
export const PipelineRunsResponse = z.object({
    runs: z.array(ConversationPipelineRunDTO),
});
/**
 * A single pipeline run on a conversation joined to its ordered execution steps.
 * Returned by `GET /api/conversations/:id/pipelines/runs/:runId` (conversation-
 * scoped; 404 when the run does not belong to the conversation). `error` carries
 * the run-level failure message for 'failed' runs.
 */
export const ConversationPipelineRunDetailDTO = z.object({
    id: z.guid(),
    name: z.string(),
    status: z.string(),
    error: z.string().nullable(),
    createdAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    /** See {@link ConversationPipelineRunDTO}.waitDeadline — only set while `waiting`. */
    waitDeadline: z.string().datetime().nullable().default(null),
    steps: z.array(PipelineRunStepDTO),
});
/**
 * Request body for `POST /api/conversations/:id/reclassify` — manually trigger
 * a re-classification of a conversation. `scope` determines the input: `first_message`
 * classifies only the initial message; `full_thread` includes all messages.
 */
export const ReclassifyRequest = z.object({
    scope: ReclassifyScopeSchema,
});
/** Тело `POST /api/conversations/:id/sender-capacity`. `null` = вернуть решение модели. */
export const SenderCapacityOverrideRequest = z.object({
    value: SenderCapacitySchema.nullable(),
});
//# sourceMappingURL=conversations.js.map